import React, { useEffect, useMemo, useState } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { IonContent, IonPage } from "@ionic/react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
  Avatar,
  Alert,
  useMediaQuery,
  useTheme as useMuiTheme,
  IconButton,
} from "@mui/material";
import {
  AdminPanelSettings,
  Dashboard,
  FolderOpen,
  GetApp,
  Home,
  Logout,
  People,
  Refresh,
  School,
  Search,
  Settings,
  Group,
  HowToReg,
  LocationOn as LocationIcon,
  Assessment,
} from "@mui/icons-material";
import { createTheme, ThemeProvider } from "@mui/material";
import axios from "axios";
import { useHistory, useLocation } from "react-router-dom";
import { baseUrl, config, getUser, logOutUser } from "../../common/utils";
import SchoolActivityStats from "./SchoolActivityStats";

interface LGA {
  id?: string | number;
  lgaName: string;
  lgaCode: string;
  name?: string;
}

interface Student {
  id?: string | number;
  schoolId?: string | number;
  studentNumber?: string;
  accCode?: string;
  firstname?: string;
  lastname?: string;
  othername?: string;
  dateOfBirth?: string;
  gender?: string;
  schoolName?: string;
  schoolType?: string;
  year?: string;
  regStatus?: string | number;
  religiousType?: string;
  lgacode?: string | number;
  // Score fields
  englishTerm1?: string | number;
  englishTerm2?: string | number;
  englishTerm3?: string | number;
  arithmeticTerm1?: string | number;
  arithmeticTerm2?: string | number;
  arithmeticTerm3?: string | number;
  generalTerm1?: string | number;
  generalTerm2?: string | number;
  generalTerm3?: string | number;
  religiousTerm1?: string | number;
  religiousTerm2?: string | number;
  religiousTerm3?: string | number;
}

interface UserData {
  name: string;
  lgaCode: string;
  schoolCode: string;
}

// ============================================
// Modern Dashboard Theme - Matching AdminDashboard
// ============================================
const theme = createTheme({
  palette: {
    primary: {
      main: "#243b5a",
    },
    secondary: {
      main: "#ef4444",
    },
    success: {
      main: "#059669",
    },
    error: {
      main: "#dc2626",
    },
    info: {
      main: "#3b82f6",
    },
    warning: {
      main: "#f59e0b",
    },
    background: {
      default: "#f4f6f8",
      paper: "#ffffff",
    },
    text: {
      primary: "#1f2937",
      secondary: "#6b7280",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontSize: "1.5rem",
      fontWeight: 700,
    },
    h5: {
      fontSize: "1.25rem",
      fontWeight: 600,
    },
    h6: {
      fontSize: "1rem",
      fontWeight: 600,
    },
    body1: {
      fontSize: "0.875rem",
      color: "#6b7280",
    },
    body2: {
      fontSize: "0.875rem",
      color: "#374151",
    },
    caption: {
      fontSize: "0.75rem",
      letterSpacing: "1px",
      color: "#6b7280",
      textTransform: "uppercase",
    },
    subtitle2: {
      fontSize: "0.875rem",
      fontWeight: 600,
      color: "#ffffff",
    },
  },
  shape: {
    borderRadius: 6,
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          border: "1px solid #e5e7eb",
          boxShadow: "none",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          border: "1px solid #e5e7eb",
          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
          borderRadius: "6px",
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
            borderColor: "#243b5a",
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 500,
          padding: "6px 16px",
          borderRadius: 6,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 600,
          backgroundColor: "#f8f9fa",
          fontSize: "13px",
          color: "#4b5563",
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: "#d1d5db",
          borderWidth: "1px",
        },
      },
    },
  },
});

// ============================================
// Styled Components - Matching AdminDashboard
// ============================================
const DashboardHeader = styled(Box)(({ theme }) => ({
  backgroundColor: "#243b5a",
  padding: "16px 40px",
  display: "flex",
  flexDirection: "column",
  width: "100%",
  boxSizing: "border-box",
}));

const HeaderTopRow = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",
});

const HeaderBottomRow = styled(Box)({
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  width: "100%",
  marginTop: "8px",
});

const LogoCircle = styled(Box)({
  width: "55px",
  height: "55px",
  borderRadius: "50%",
  backgroundColor: "#ffffff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "20px",
  color: "#243b5a",
  fontWeight: "bold",
  border: "2px solid #f0c45a",
  marginRight: "16px",
});

const SchoolInfoBox = styled(Box)({
  textAlign: "right",
  color: "#f1f5f9",
});

const LogoutButton = styled(Button)({
  backgroundColor: "#ef4444",
  color: "#ffffff",
  fontWeight: 600,
  fontSize: "14px",
  textTransform: "none",
  padding: "6px 16px",
  borderRadius: "6px",
  "&:hover": {
    backgroundColor: "#dc2626",
  },
  "& svg": {
    marginRight: "8px",
  },
});

const NavMenu = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  marginTop: "12px",
  flexWrap: "wrap",
});

const NavButton = styled(Button)<{ active?: boolean }>(({ active }) => ({
  color: active ? "#ffffff" : "#e0e7ff",
  backgroundColor: active ? "rgba(255,255,255,0.15)" : "transparent",
  fontWeight: active ? 600 : 500,
  fontSize: "14px",
  textTransform: "none",
  padding: "6px 16px",
  borderRadius: "20px",
  "&:hover": {
    backgroundColor: "rgba(255,255,255,0.1)",
    color: "#ffffff",
  },
  "& svg": {
    marginRight: "8px",
    fontSize: "18px",
  },
}));

const SummaryCard = styled(Card)({
  borderRadius: 12,
  boxShadow: "0 2px 10px rgba(15, 23, 42, 0.08)",
});

const apiService = {
  getLGAs: async (): Promise<LGA[]> => {
    const response = await axios.post(`${baseUrl()}Admin2s/getLGA`, config);
    return response.data.data || [];
  },
  exportStudentsByLGA: async (lgaCode: string): Promise<any> => {
    const response = await axios.post(`${baseUrl()}Admin2s/exportStudentsByLGA`, { lgaCode }, config);
    return response.data;
  },
};

const getFullName = (student: Student) => {
  const first = student.firstname?.trim() || "";
  const last = student.lastname?.trim() || "";
  const other = student.othername?.trim() || "";
  return [last, first, other].filter(Boolean).join(" ");
};

const getReligiousType = (value?: string) => {
  if (value === "1" || value === "Christian" || value === "CRS") return "Christian";
  if (value === "2" || value === "Islam" || value === "IRS") return "Islam";
  return value || "Unknown";
};

const formatScores = (t1?: any, t2?: any, t3?: any) => {
  return [t1 || "0", t2 || "0", t3 || "0"].join("/");
};

const getRegStatusLabel = (val?: string | number) => {
  const s = String(val);
  if (s === "1") return "Normal";
  if (s === "2") return "Late";
  if (s === "3") return "Post Late";
  return "Unknown";
};

const LGAStudentDatabase: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));

  const [lgas, setLgas] = useState<LGA[]>([]);
  const [selectedLGA, setSelectedLGA] = useState<string>("");
  const [loadingLGAs, setLoadingLGAs] = useState(false);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [lgaStudents, setLgaStudents] = useState<Student[]>([]);
  const [groupedStudents, setGroupedStudents] = useState<Map<string, Student[]>>(new Map());
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [regStatusFilter, setRegStatusFilter] = useState<string>("All");
  const [message, setMessage] = useState<{ type: "success" | "error" | ""; text: string }>({ type: "", text: "" });

  const user = getUser();
  const userData: UserData = {
    name: user?.name || "ADMIN USER",
    lgaCode: user?.lgaCode || "",
    schoolCode: user?.schoolCode || "",
  };

  useEffect(() => {
    fetchLGAs();
  }, []);

  const fetchLGAs = async () => {
    setLoadingLGAs(true);
    try {
      const data = await apiService.getLGAs();
      setLgas(data || []);
      setMessage({ type: "success", text: "LGAs loaded successfully." });
    } catch (error) {
      console.error("Error fetching LGAs:", error);
      setMessage({ type: "error", text: "Unable to load LGAs." });
    } finally {
      setLoadingLGAs(false);
    }
  };

  const fetchLgaStudents = async (lgaCode: string) => {
    if (!lgaCode) {
      setMessage({ type: "error", text: "Please select an LGA first." });
      return;
    }
    setLoadingStudents(true);
    try {
      const response = await apiService.exportStudentsByLGA(lgaCode);
      let data = response;
      if (response && response.status && response.data) {
        data = response.data;
      }
      const students: Student[] = Array.isArray(data) ? data : [];
      setLgaStudents(students);

      const grouped = new Map<string, Student[]>();
      students.forEach((student) => {
        const schoolName = student.schoolName || "Unknown School";
        if (!grouped.has(schoolName)) {
          grouped.set(schoolName, []);
        }
        grouped.get(schoolName)!.push(student);
      });
      setGroupedStudents(grouped);

      setMessage({ type: "success", text: `Loaded ${students.length} students from ${grouped.size} schools.` });
    } catch (error) {
      console.error("Error fetching LGA students:", error);
      setLgaStudents([]);
      setGroupedStudents(new Map());
      setMessage({ type: "error", text: "Unable to load LGA student data." });
    } finally {
      setLoadingStudents(false);
    }
  };

  const handleLGAChange = async (event: SelectChangeEvent<string>) => {
    const value = event.target.value as string;
    setSelectedLGA(value);
    setSearchTerm("");
    setRegStatusFilter("All");
    setLgaStudents([]);
    setGroupedStudents(new Map());
    setMessage({ type: "", text: "" });
    if (value) {
      await fetchLgaStudents(value);
    }
  };

  const filteredGroupedStudents = useMemo(() => {
    if (!searchTerm.trim() && regStatusFilter === "All") {
      return groupedStudents;
    }
    const filtered = new Map<string, Student[]>();
    const term = searchTerm.toLowerCase();
    groupedStudents.forEach((students, schoolName) => {
      const matches = students.filter((student) => {
        const fullName = getFullName(student).toLowerCase();
        const matchesSearch = !term || (
          schoolName.toLowerCase().includes(term) ||
          fullName.includes(term) ||
          student.studentNumber?.toLowerCase().includes(term) ||
          student.accCode?.toLowerCase().includes(term) ||
          student.gender?.toLowerCase().includes(term) ||
          getReligiousType(student.religiousType).toLowerCase().includes(term)
        );
        const sRegStatus = String(student.regStatus);
        const matchesStatus = regStatusFilter === "All" || sRegStatus === regStatusFilter;
        return matchesSearch && matchesStatus;
      });
      if (matches.length > 0) {
        filtered.set(schoolName, matches);
      }
    });
    return filtered;
  }, [groupedStudents, searchTerm, regStatusFilter]);

  const handleMenuClick = (menu: string) => {
    history.push(`/${menu}`);
  };

  const handleLogout = () => {
    logOutUser();
    history.push("/login");
  };

  const handleRefresh = async () => {
    await fetchLGAs();
    if (selectedLGA) {
      await fetchLgaStudents(selectedLGA);
    }
  };

  // Helpers for Merge CSV
  const getMergeSchoolType = (type: any) => {
    const typeText = String(type).toLowerCase();
    if (typeText.includes("public")) return "0";
    return "1";
  };
  
  const getMergeReligiousType = (type: any) => {
    const relText = String(getReligiousType(type)).toLowerCase();
    if (relText.includes("islam") || relText.includes("muslim")) return "2";
    return "1";
  };
  
  const getScore = (val: any) => (val === null || val === undefined || val === "") ? "-" : val;

  const exportToCSV = (exportFiltered: boolean = false) => {
    let dataToExport = lgaStudents;
    if (exportFiltered) {
      dataToExport = Array.from(filteredGroupedStudents.values()).flat();
    }
    
    if (dataToExport.length === 0) {
      setMessage({ type: "error", text: "No student data available to export." });
      return;
    }

    const escapeCSV = (value: any) => {
      if (value === null || value === undefined) return '""';
      const str = String(value);
      return `"${str.replace(/"/g, '""')}"`;
    };

    const headers = [
      "School Name",
      "School ID",
      "LGA Code",
      "School Type",
      "Year",
      "Exam No",
      "Access Code",
      "Last Name",
      "First Name",
      "Other Names",
      "Gender",
      "Date of Birth",
      "English T1", "English T2", "English T3",
      "Arithmetic T1", "Arithmetic T2", "Arithmetic T3",
      "General T1", "General T2", "General T3",
      "Religious T1", "Religious T2", "Religious T3",
      "Religion",
      "Registration Status",
    ];

    const rows = dataToExport.map((student) => [
      student.schoolName || "Unknown School",
      student.schoolId || "",
      student.lgacode || "",
      student.schoolType || "N/A",
      student.year || "N/A",
      student.studentNumber || "",
      student.accCode || "",
      student.lastname || "",
      student.firstname || "",
      student.othername || "",
      student.gender || "",
      student.dateOfBirth || "",
      student.englishTerm1 || "0", student.englishTerm2 || "0", student.englishTerm3 || "0",
      student.arithmeticTerm1 || "0", student.arithmeticTerm2 || "0", student.arithmeticTerm3 || "0",
      student.generalTerm1 || "0", student.generalTerm2 || "0", student.generalTerm3 || "0",
      student.religiousTerm1 || "0", student.religiousTerm2 || "0", student.religiousTerm3 || "0",
      getReligiousType(student.religiousType),
      getRegStatusLabel(student.regStatus),
    ]);

    const csvContent = [headers.map(escapeCSV), ...rows.map(row => row.map(escapeCSV))].map(e => e.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const prefix = exportFiltered ? "Filtered_" : "";
    link.setAttribute("download", `${prefix}lga_student_database_${selectedLGA || "data"}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportMergeCSV = (exportFiltered: boolean = false) => {
    let dataToExport = lgaStudents;
    if (exportFiltered) {
      dataToExport = Array.from(filteredGroupedStudents.values()).flat();
    }
    
    if (dataToExport.length === 0) {
      setMessage({ type: "error", text: "No student data available to export." });
      return;
    }

    const escapeCSV = (value: any) => {
      if (value === null || value === undefined) return '""';
      const str = String(value);
      return `"${str.replace(/"/g, '""')}"`;
    };

    const headers = [
      "S/N",
      "school_session",
      "progID",
      "Reg. No",
      "Surename",
      "Other Name(s)",
      "First Name",
      "Gender",
      "RGSY1", "RGSY2", "RGSY3",
      "GPY1", "GPY2", "GPY3",
      "ARITY1", "ARITY2", "ARITY3",
      "ENGY1", "ENGY2", "ENGY3",
      "rgsType",
      "schType",
      "schcode",
      "lgacode"
    ];

    const rows = dataToExport.map((student, index) => [
      index + 1,
      student.year || "2024/2025",
      "1",
      student.studentNumber || "",
      student.lastname || "",
      student.firstname || "",
      student.othername || "",
      student.gender || "",
      getScore(student.religiousTerm1), getScore(student.religiousTerm2), getScore(student.religiousTerm3),
      getScore(student.generalTerm1), getScore(student.generalTerm2), getScore(student.generalTerm3),
      getScore(student.arithmeticTerm1), getScore(student.arithmeticTerm2), getScore(student.arithmeticTerm3),
      getScore(student.englishTerm1), getScore(student.englishTerm2), getScore(student.englishTerm3),
      getMergeReligiousType(student.religiousType),
      getMergeSchoolType(student.schoolType),
      student.schoolId || "",
      student.lgacode || ""
    ]);

    const csvContent = [headers.map(escapeCSV), ...rows.map(row => row.map(escapeCSV))].map(e => e.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const prefix = exportFiltered ? "Filtered_" : "";
    link.setAttribute("download", `${prefix}merge_lga_database_${selectedLGA || "data"}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const generateAccessCodePDF = (studentsToExport: Student[], exportPrefix: string) => {
    if (studentsToExport.length === 0) {
      setMessage({ type: "error", text: "No student data available to export." });
      return;
    }

    const doc = new jsPDF("landscape");
    const pageWidth = doc.internal.pageSize.width;

    // Group students by schoolName
    const grouped = new Map<string, Student[]>();
    studentsToExport.forEach((student) => {
      const schoolName = student.schoolName || "Unknown School";
      if (!grouped.has(schoolName)) {
        grouped.set(schoolName, []);
      }
      grouped.get(schoolName)!.push(student);
    });

    const lgaObj = lgas.find(l => l.lgaCode === selectedLGA);
    const lgaCodeVal = lgaObj?.lgaCode || selectedLGA || "N/A";

    let isFirstSchool = true;

    grouped.forEach((studentsInSchool, schoolName) => {
      if (!isFirstSchool) {
        doc.addPage();
      }
      isFirstSchool = false;

      const schoolCode = studentsInSchool[0]?.schoolId || "N/A";
      const year = studentsInSchool[0]?.year || "2025/2026";

      // Table Data
      const tableData = studentsInSchool.map((student, index) => {
        const sex = student.gender?.toLowerCase() === "male" ? "M" : (student.gender?.toLowerCase() === "female" ? "F" : student.gender || "");
        return [
          index + 1,
          student.studentNumber || "",
          getFullName(student),
          sex,
          student.accCode || ""
        ];
      });

      let isFirstTablePage = true;
      let pageCountForSchool = 1;

      autoTable(doc, {
        startY: 40,
        head: [["S/N", "EXAMINATION\nNO.", "NAMES", "SEX", "ACCESS CODE"]],
        body: tableData,
        theme: 'plain',
        headStyles: {
          fillColor: false,
          textColor: [0, 0, 0],
          fontStyle: 'bold',
          lineWidth: 0.1,
          lineColor: [0, 0, 0]
        },
        bodyStyles: {
          textColor: [0, 0, 0],
          lineWidth: 0.1,
          lineColor: [0, 0, 0]
        },
        styles: { fontSize: 9, cellPadding: 2, halign: 'center' },
        columnStyles: {
          0: { halign: 'center', cellWidth: 15 },
          1: { halign: 'center', cellWidth: 35 },
          2: { halign: 'left', cellWidth: 'auto' },
          3: { halign: 'center', cellWidth: 20 },
          4: { halign: 'center', cellWidth: 40 }
        },
        didDrawPage: function (data) {
          if (isFirstTablePage) {
            // Main Header
            doc.setFontSize(14);
            doc.setFont("helvetica", "bold");
            doc.text("MINISTRY OF PRIMARY EDUCATION, ASABA DELTA STATE", pageWidth / 2, 15, { align: "center" });
            
            doc.setFontSize(11);
            doc.setFont("helvetica", "normal");
            doc.text(`LGA CODE: ${lgaCodeVal} :: SCHOOL CODE: ${schoolCode} : ${schoolName}`, pageWidth / 2, 22, { align: "center" });
            
            doc.setFont("helvetica", "bold");
            doc.text(`${year} COGNITIVE/PLACEMENT EXAMINATION FOR PRIMARY SIX PUPILS`, pageWidth / 2, 29, { align: "center" });

            isFirstTablePage = false;
          } else {
            pageCountForSchool++;
            // Continued Header
            doc.setFontSize(11);
            doc.setFont("helvetica", "normal");
            doc.text(`${schoolName} — continued (page ${pageCountForSchool})`, pageWidth / 2, 20, { align: "center" });
          }
        },
        margin: { top: 40 }
      });
    });

    const safePrefixName = exportPrefix.replace(/[^a-z0-9]/gi, '_').toLowerCase();
    doc.save(`AccessCode_${safePrefixName}.pdf`);
  };

  const exportToPDF = (exportFiltered: boolean = false) => {
    let dataToExport = lgaStudents;
    if (exportFiltered) {
      dataToExport = Array.from(filteredGroupedStudents.values()).flat();
    }
    
    const lgaObj = lgas.find(l => l.lgaCode === selectedLGA);
    const lgaName = lgaObj?.name || lgaObj?.lgaName || selectedLGA || "LGA";
    const prefix = exportFiltered ? "Filtered_" : "";
    
    generateAccessCodePDF(dataToExport, `${prefix}${lgaName}`);
  };

  const exportSchoolToPDF = (schoolName: string, students: Student[]) => {
    generateAccessCodePDF(students, schoolName);
  };

  const flattenedStudents = useMemo(() => Array.from(filteredGroupedStudents.values()).flat(), [filteredGroupedStudents]);
  
  const totalSchools = flattenedStudents.length > 0 ? new Set(flattenedStudents.map((student) => student.schoolName || "Unknown School")).size : 0;
  const totalMale = flattenedStudents.filter((student) => student.gender?.toLowerCase() === "male").length;
  const totalFemale = flattenedStudents.filter((student) => student.gender?.toLowerCase() === "female").length;
  const totalActive = flattenedStudents.filter((student) => String(student.regStatus) === "1").length;
  const totalStudents = flattenedStudents.length;

  const pageTitle = "LGA Student Database";

  return (
    <ThemeProvider theme={theme}>
      <IonPage>
        <IonContent>
          <Box sx={{
            bgcolor: "background.default",
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
          }}>
            {/* HEADER - Matching AdminDashboard */}
            <DashboardHeader>
              <HeaderTopRow>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <LogoCircle>⚖️</LogoCircle>
                  <Box>
                    <Typography variant="caption" sx={{ color: "#cbd5e1", letterSpacing: "0.5px", display: "block" }}>
                      DELTA STATE GOVERNMENT
                    </Typography>
                    <Typography variant="h6" sx={{ color: "#ffffff", fontWeight: 600, lineHeight: 1.2 }}>
                      Ministry of Primary Education
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db" }}>
                      LGA Student Database Dashboard
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <SchoolInfoBox>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, color: "#ffffff", textTransform: "uppercase" }}
                    >
                      {userData.name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db" }}>
                      LGA: {userData.lgaCode} | Code: {userData.schoolCode}
                    </Typography>
                  </SchoolInfoBox>
                  <IconButton
                    onClick={handleRefresh}
                    title="Refresh Data"
                    sx={{
                      color: "#ffffff",
                      bgcolor: "rgba(255, 255, 255, 0.1)",
                      "&:hover": { bgcolor: "rgba(255, 255, 255, 0.2)" },
                    }}
                  >
                    <Refresh />
                  </IconButton>
                </Box>
              </HeaderTopRow>

              <HeaderBottomRow>
                <LogoutButton
                  variant="contained"
                  onClick={handleLogout}
                  startIcon={<Logout />}
                >
                  Logout
                </LogoutButton>
              </HeaderBottomRow>

              {/* Navigation Menu - Matching AdminDashboard */}
              <NavMenu>
                <NavButton
                  active={location.pathname === "/admin_dashboard"}
                  onClick={() => handleMenuClick("admin_dashboard")}
                  startIcon={<Dashboard />}
                >
                  Admin Dashboard
                </NavButton>
                <NavButton
                  active={location.pathname === "/admin_students"}
                  onClick={() => handleMenuClick("admin_students")}
                  startIcon={<People />}
                >
                  Students
                </NavButton>
                <NavButton
                  active={location.pathname === "/lga_student_database"}
                  onClick={() => handleMenuClick("lga_student_database")}
                  startIcon={<Group />}
                >
                  LGA Student Database
                </NavButton>
                <NavButton
                  active={location.pathname === "/admin_schools"}
                  onClick={() => handleMenuClick("admin_schools")}
                  startIcon={<School />}
                >
                  View Schools
                </NavButton>
                <NavButton
                  active={location.pathname === "/add_result"}
                  onClick={() => handleMenuClick("add_result")}
                  startIcon={<HowToReg />}
                >
                  Add Result
                </NavButton>
                <NavButton
                  active={location.pathname === "/admin_settings"}
                  onClick={() => handleMenuClick("admin_settings")}
                  startIcon={<Settings />}
                >
                  Settings
                </NavButton>
                <NavButton
                  active={location.pathname === "/admin_management"}
                  onClick={() => handleMenuClick("admin_management")}
                  startIcon={<AdminPanelSettings />}
                >
                  Management Console
                </NavButton>

                <NavButton
                  active={location.pathname === "/admin_stats"}
                  onClick={() => handleMenuClick("admin_stats")}
                  startIcon={<Assessment />}
                >
                  Activity Stats
                </NavButton>
              </NavMenu>
            </DashboardHeader>


            {/* MAIN CONTENT */}
            <Container maxWidth="xl" sx={{ py: 4, flex: 1 }}>
              {/* Header */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h4" sx={{ fontWeight: 800, color: "#1a237e" }}>
                  {pageTitle}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Browse students by Local Government Area, grouped by school, and export structured CSV data.
                </Typography>
              </Box>

          {message.text && (
            <Box sx={{ mt: 3 }}>
              <Alert severity={message.type || "info"}>{message.text}</Alert>
            </Box>
          )}

          <Card sx={{ mt: 3, mb: 3, p: 2 }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel id="lga-select-label">Select LGA</InputLabel>
                    <Select
                      labelId="lga-select-label"
                      value={selectedLGA}
                      label="Select LGA"
                      onChange={handleLGAChange}
                      disabled={loadingLGAs}
                      startAdornment={
                        <LocationIcon sx={{ mr: 1, color: "action.active" }} />
                      }
                    >
                      <MenuItem value="">Choose an LGA</MenuItem>
                      {lgas && lgas.map((lga) => (
                        <MenuItem key={lga.lgaCode || lga.id} value={lga.lgaCode}>
                          {lga.name || lga.lgaName} - ({lga.lgaCode})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Stack direction={isMobile ? "column" : "row"} spacing={2}>
                    <Button
                      variant="contained"
                      startIcon={<People />}
                      onClick={() => selectedLGA && fetchLgaStudents(selectedLGA)}
                      disabled={!selectedLGA || loadingStudents}
                    >
                      Load Student Database
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Refresh />}
                      onClick={handleRefresh}
                      disabled={loadingLGAs || loadingStudents}
                    >
                      Reload
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {selectedLGA && (
            <Box sx={{ mb: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <SummaryCard>
                    <CardContent>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: "text.secondary" }}>
                        Total Students
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800 }}>
                        {totalStudents}
                      </Typography>
                    </CardContent>
                  </SummaryCard>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <SummaryCard>
                    <CardContent>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: "text.secondary" }}>
                        Total Schools
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800 }}>
                        {totalSchools}
                      </Typography>
                    </CardContent>
                  </SummaryCard>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <SummaryCard>
                    <CardContent>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: "text.secondary" }}>
                        Male Students
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800 }}>
                        {totalMale}
                      </Typography>
                    </CardContent>
                  </SummaryCard>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <SummaryCard>
                    <CardContent>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: "text.secondary" }}>
                        Female Students
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800 }}>
                        {totalFemale}
                      </Typography>
                    </CardContent>
                  </SummaryCard>
                </Grid>
              </Grid>
            </Box>
          )}

          {selectedLGA && lgaStudents.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Stack direction={isMobile ? "column" : "row"} spacing={2} alignItems="center" mb={2}>
                <FormControl sx={{ minWidth: 200 }} size="small">
                  <InputLabel id="reg-status-select-label">Registration Status</InputLabel>
                  <Select
                    labelId="reg-status-select-label"
                    value={regStatusFilter}
                    label="Registration Status"
                    onChange={(e) => setRegStatusFilter(e.target.value)}
                  >
                    <MenuItem value="All">All Status</MenuItem>
                    <MenuItem value="1">Normal</MenuItem>
                    <MenuItem value="2">Late</MenuItem>
                    <MenuItem value="3">Post Late</MenuItem>
                  </Select>
                </FormControl>
                
                <TextField
                  fullWidth
                  placeholder="Search by school, student name, gender or religion"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>
              
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#243b5a" }}>
                  Student Records
                </Typography>
                
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  {(searchTerm !== "" || regStatusFilter !== "All") && (
                    <>
                      <Button variant="contained" color="secondary" startIcon={<GetApp />} onClick={() => exportMergeCSV(true)} sx={{ borderRadius: "8px", textTransform: "none" }}>
                        Export Filtered Merge CSV
                      </Button>
                      <Button variant="contained" color="secondary" startIcon={<GetApp />} onClick={() => exportToCSV(true)} sx={{ borderRadius: "8px", textTransform: "none" }}>
                        Export Filtered CSV
                      </Button>
                      <Button variant="contained" color="primary" startIcon={<GetApp />} onClick={() => exportToPDF(true)} sx={{ borderRadius: "8px", textTransform: "none" }}>
                        Download Filtered Access Code
                      </Button>
                    </>
                  )}
                  
                  <Button variant="outlined" startIcon={<GetApp />} onClick={() => exportMergeCSV(false)} sx={{ borderRadius: "8px", textTransform: "none" }}>
                    Export All Merge CSV
                  </Button>
                  <Button variant="outlined" startIcon={<GetApp />} onClick={() => exportToCSV(false)} sx={{ borderRadius: "8px", textTransform: "none" }}>
                    Export All CSV
                  </Button>
                  <Button variant="outlined" startIcon={<GetApp />} onClick={() => exportToPDF(false)} sx={{ borderRadius: "8px", textTransform: "none" }}>
                    Download All Access Code
                  </Button>
                </Box>
              </Box>
            </Box>
          )}

          {loadingStudents && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading student records...</Typography>
            </Box>
          )}

          {selectedLGA && !loadingStudents && lgaStudents.length === 0 && (
            <Alert severity="info" sx={{ mb: 3 }}>
              No student records loaded for this LGA yet. Select an LGA and click "Load Student Database".
            </Alert>
          )}

          {selectedLGA && lgaStudents.length > 0 && (
            <Box>
              {filteredGroupedStudents.size === 0 ? (
                <Alert severity="info">
                  No matching student records found for the current filter.
                </Alert>
              ) : (
                Array.from(filteredGroupedStudents.entries()).map(([schoolName, students]) => (
                  <Card key={schoolName} sx={{ mb: 3 }}>
                    <CardContent>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {schoolName}
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                          <Chip label={`${students.length} students`} color="primary" />
                          <Button 
                            variant="outlined" 
                            size="small" 
                            color="primary"
                            startIcon={<GetApp />}
                            onClick={() => exportSchoolToPDF(schoolName, students)}
                            sx={{ borderRadius: "6px", textTransform: "none" }}
                          >
                            Download Access Code
                          </Button>
                        </Box>
                      </Box>

                      <TableContainer component={Paper} sx={{ boxShadow: "none" }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>#</TableCell>
                              <TableCell>School Name</TableCell>
                              <TableCell>School ID</TableCell>
                              <TableCell>LGA Code</TableCell>
                              <TableCell>Type</TableCell>
                              <TableCell>Year</TableCell>
                              <TableCell>Exam No</TableCell>
                              <TableCell>Access Code</TableCell>
                              <TableCell>Last Name</TableCell>
                              <TableCell>First Name</TableCell>
                              <TableCell>Other Names</TableCell>
                              <TableCell>Gender</TableCell>
                              <TableCell>DOB</TableCell>
                              <TableCell>Eng T1</TableCell>
                              <TableCell>Eng T2</TableCell>
                              <TableCell>Eng T3</TableCell>
                              <TableCell>Arith T1</TableCell>
                              <TableCell>Arith T2</TableCell>
                              <TableCell>Arith T3</TableCell>
                              <TableCell>Gen T1</TableCell>
                              <TableCell>Gen T2</TableCell>
                              <TableCell>Gen T3</TableCell>
                              <TableCell>Rel T1</TableCell>
                              <TableCell>Rel T2</TableCell>
                              <TableCell>Rel T3</TableCell>
                              <TableCell>Religion</TableCell>
                              <TableCell>Status</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {students.map((student, index) => (
                              <TableRow key={student.id ?? index}>
                                <TableCell>{index + 1}</TableCell>
                                <TableCell sx={{ fontWeight: 500, whiteSpace: "nowrap" }}>{student.schoolName || schoolName}</TableCell>
                                <TableCell>{student.schoolId || "N/A"}</TableCell>
                                <TableCell>{student.lgacode || "N/A"}</TableCell>
                                <TableCell>{student.schoolType || "N/A"}</TableCell>
                                <TableCell>{student.year || "N/A"}</TableCell>
                                <TableCell>{student.studentNumber || "N/A"}</TableCell>
                                <TableCell sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>{student.accCode || "N/A"}</TableCell>
                                <TableCell sx={{ whiteSpace: "nowrap" }}>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Avatar sx={{ width: 22, height: 22, bgcolor: "#243b5a", fontSize: 10 }}>
                                      {student.lastname?.charAt(0).toUpperCase() || "S"}
                                    </Avatar>
                                    <Typography variant="body2" sx={{ fontSize: "0.8rem", fontWeight: 500 }}>
                                      {student.lastname || "N/A"}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell sx={{ whiteSpace: "nowrap" }}>{student.firstname || "N/A"}</TableCell>
                                <TableCell sx={{ whiteSpace: "nowrap" }}>{student.othername || "N/A"}</TableCell>
                                <TableCell>{student.gender || "N/A"}</TableCell>
                                <TableCell sx={{ whiteSpace: "nowrap" }}>{student.dateOfBirth || "N/A"}</TableCell>
                                <TableCell sx={{ textAlign: "center" }}>{student.englishTerm1 || "0"}</TableCell>
                                <TableCell sx={{ textAlign: "center" }}>{student.englishTerm2 || "0"}</TableCell>
                                <TableCell sx={{ textAlign: "center" }}>{student.englishTerm3 || "0"}</TableCell>
                                <TableCell sx={{ textAlign: "center" }}>{student.arithmeticTerm1 || "0"}</TableCell>
                                <TableCell sx={{ textAlign: "center" }}>{student.arithmeticTerm2 || "0"}</TableCell>
                                <TableCell sx={{ textAlign: "center" }}>{student.arithmeticTerm3 || "0"}</TableCell>
                                <TableCell sx={{ textAlign: "center" }}>{student.generalTerm1 || "0"}</TableCell>
                                <TableCell sx={{ textAlign: "center" }}>{student.generalTerm2 || "0"}</TableCell>
                                <TableCell sx={{ textAlign: "center" }}>{student.generalTerm3 || "0"}</TableCell>
                                <TableCell sx={{ textAlign: "center" }}>{student.religiousTerm1 || "0"}</TableCell>
                                <TableCell sx={{ textAlign: "center" }}>{student.religiousTerm2 || "0"}</TableCell>
                                <TableCell sx={{ textAlign: "center" }}>{student.religiousTerm3 || "0"}</TableCell>
                                <TableCell>{getReligiousType(student.religiousType)}</TableCell>
                                <TableCell>
                                  <Chip 
                                    label={getRegStatusLabel(student.regStatus)} 
                                    size="small" 
                                    sx={{ 
                                      height: 18, 
                                      fontSize: "0.65rem",
                                      bgcolor: String(student.regStatus) === "1" ? "#e3f2fd" : "#fff3e0",
                                      color: String(student.regStatus) === "1" ? "#1976d2" : "#ed6c02"
                                    }} 
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Card>
                ))
              )}
            </Box>
          )}
        </Container>
      </Box>
    </IonContent>
  </IonPage>
</ThemeProvider>
);
};

export default LGAStudentDatabase;
